import React, { useState } from 'react';
import { Alert, Button, Drawer, Empty, Popconfirm, Space, Spin, Tag, Tooltip, Typography, message } from 'antd';
import {
    PlusOutlined,
    RobotOutlined,
    TranslationOutlined,
    SendOutlined,
    BranchesOutlined,
    UserAddOutlined,
    DownloadOutlined,
    EditOutlined,
} from '@ant-design/icons';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import ModuleCard from './ModuleCard';
import ModuleFormModal from './ModuleFormModal';
import TranslateModal from './TranslateModal';
import CourseFormModal from './CourseFormModal';
import EnrollModal from './EnrollModal';
import { downloadBlob } from '../../utils/downloadBlob';
import {
    COURSE_STATUS_LABELS,
    canAuthorTraining,
    canPublishTraining,
    courseHasAnyUnreviewedAiModule,
    pendingTranslationCount,
    languageLabel,
    formatDdMmYyyy,
    englishOnlyNote,
    TRANSLATIONS_LOCKED_MESSAGE,
} from '../../utils/trainingModel';

const { Text, Title } = Typography;

const STATUS_COLOURS = { draft: 'default', published: 'green', retired: 'red' };

/**
 * Course detail for the authoring personas. Every action is gated twice: hidden here by
 * persona / course status, and refused by the API (training.controller.ts) regardless.
 *
 *   Advisor + Lead Advisor : edit (draft), modules, AI generate, translate + review, fork,
 *                            enrol (published), SCORM export
 *   Lead Advisor only      : publish  (design doc: "LA publishes")
 *
 * Commandment IV: Publish stays disabled while any AI-drafted module is still unreviewed.
 * (The API enforces the same rule in assertCoursePublishAllowed; this guard just explains it.)
 */
const CourseDrawer = ({ open, projectId, courseId, role, members, onClose, onChanged, onOpenCourse }) => {
    const { request } = useApiRequest();
    const canAuthor = canAuthorTraining(role);
    const canPublish = canPublishTraining(role);

    const detail = useResource(
        async () => (await request('GET', `/projects/${projectId}/training/courses/${courseId}`)).data.course,
        [projectId, courseId, request],
        { enabled: open && !!courseId },
    );
    const course = detail.data;

    const [busy, setBusy] = useState(null);
    const [moduleModal, setModuleModal] = useState({ open: false, module: null });
    const [translateOpen, setTranslateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [enrollOpen, setEnrollOpen] = useState(false);

    const refresh = () => {
        detail.reload();
        onChanged();
    };

    const run = async (key, fn, successMessage) => {
        setBusy(key);
        try {
            const result = await fn();
            if (successMessage) message.success(typeof successMessage === 'function' ? successMessage(result) : successMessage);
            return result;
        } catch (err) {
            message.error(err.message || 'That action failed.');
            return null;
        } finally {
            setBusy(null);
        }
    };

    const base = `/projects/${projectId}/training/courses/${courseId}`;

    const generate = async () => {
        const result = await run(
            'generate',
            () => request('POST', `${base}/generate-content`),
            (r) => `${(r.data.modules || []).length} AI-drafted module(s) added - review each before publishing.`,
        );
        if (result) refresh();
    };

    const publish = async () => {
        const result = await run('publish', () => request('POST', `${base}/publish`), 'Course published.');
        if (result) refresh();
    };

    const fork = async () => {
        const result = await run('fork', () => request('POST', `${base}/new-version`), 'New draft version created.');
        if (result) {
            onChanged();
            if (onOpenCourse) onOpenCourse(result.data.course.id);
        }
    };

    const exportScorm = async () => {
        await run(
            'export',
            async () => {
                const { blob, filename, headers } = await request('POST', `${base}/export`, { query: { format: 'scorm' }, expect: 'blob' });
                downloadBlob(blob, filename || `${courseId}-scorm12.zip`);
                let skipped = 0;
                try {
                    const raw = headers && headers.get && headers.get('X-Training-Scorm-Skipped-Modules');
                    if (raw) {
                        const parsed = JSON.parse(decodeURIComponent(raw));
                        skipped = typeof parsed?.total === 'number' ? parsed.total : Array.isArray(parsed) ? parsed.length : 0;
                    }
                } catch {
                    skipped = 0;
                }
                if (skipped > 0) message.info(`${skipped} video / live-session module(s) are not included in a SCORM 1.2 package.`);
            },
            'SCORM 1.2 package downloaded (English text only; content pages are static and do not report completion to an LMS).',
        );
    };

    const isDraft = course?.status === 'draft';
    const isPublished = course?.status === 'published';
    const unreviewedAi = courseHasAnyUnreviewedAiModule(course);
    const pendingTranslations = pendingTranslationCount(course);
    const videosMissingFile = (course?.modules || []).filter((m) => m.moduleType === 'video' && !m.contentBlobUrl);

    const publishButton = (
        <Button type="primary" icon={<SendOutlined />} loading={busy === 'publish'} disabled={unreviewedAi || videosMissingFile.length > 0 || (course?.modules || []).length === 0}>
            Publish
        </Button>
    );

    return (
        <Drawer open={open} width={860} onClose={onClose} title={course ? course.title : 'Course'} destroyOnClose>
            {detail.status === 'loading' && !course && (
                <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
            )}
            {detail.status === 'error' && (
                <Alert
                    type="error"
                    showIcon
                    message="Could not load this course"
                    description={detail.error?.message}
                    action={<Button size="small" onClick={detail.reload}>Retry</Button>}
                />
            )}
            {course && (
                <div data-testid="course-drawer-body">
                    <Space wrap style={{ marginBottom: 8 }}>
                        <Tag color={STATUS_COLOURS[course.status] || 'default'}>{COURSE_STATUS_LABELS[course.status] || course.status}</Tag>
                        <Tag>v{course.version}</Tag>
                        {(course.controlRefs || []).map((c) => <Tag key={c} color="blue">{c}</Tag>)}
                        {isPublished && <Text type="secondary">Published {formatDdMmYyyy(course.publishedAt)}</Text>}
                    </Space>
                    <Text style={{ display: 'block', marginBottom: 4 }}>{course.description}</Text>
                    {course.targetAudience && <Text type="secondary" style={{ display: 'block' }}>Audience: {course.targetAudience}</Text>}
                    {typeof course.passMarkPercent === 'number' && (
                        <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>Quiz pass mark: {course.passMarkPercent}%{typeof course.maxAttempts === 'number' ? ` · up to ${course.maxAttempts} attempts` : ''}</Text>
                    )}
                    {isPublished && (
                        <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                            Languages learners can choose: {(course.selectableLanguages || ['en']).map(languageLabel).join(', ')}. A language is offered only while every module has an approved translation of its current source text.
                        </Text>
                    )}

                    {canAuthor && (
                        <Space wrap style={{ margin: '16px 0' }}>
                            {isDraft && (
                                <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>Edit details</Button>
                            )}
                            {isDraft && (
                                <Button icon={<PlusOutlined />} onClick={() => setModuleModal({ open: true, module: null })}>Add module</Button>
                            )}
                            {isDraft && (
                                <Popconfirm
                                    title="Draft modules with AI?"
                                    description="AI adds reading and quiz modules from this course's control references. They are drafts: you must review each one before the course can be published."
                                    okText="Generate drafts"
                                    onConfirm={generate}
                                >
                                    <Button icon={<RobotOutlined />} loading={busy === 'generate'}>Generate content with AI</Button>
                                </Popconfirm>
                            )}
                            {isDraft && (course.modules || []).length > 0 && (
                                <Button icon={<TranslationOutlined />} onClick={() => setTranslateOpen(true)}>Translate</Button>
                            )}
                            {isPublished && (
                                <Button type="primary" icon={<UserAddOutlined />} onClick={() => setEnrollOpen(true)}>Enrol learners</Button>
                            )}
                            {isPublished && (
                                <Popconfirm
                                    title="Fork a new draft version?"
                                    description="A published course cannot be edited. This creates v+1 as a draft (modules copied, translations and reviews not carried over). Existing enrolments stay on this version."
                                    okText="Create new version"
                                    onConfirm={fork}
                                >
                                    <Button icon={<BranchesOutlined />} loading={busy === 'fork'}>New version</Button>
                                </Popconfirm>
                            )}
                            {isPublished && (course.modules || []).length > 0 && (
                                <Button icon={<DownloadOutlined />} loading={busy === 'export'} onClick={exportScorm}>Export SCORM 1.2</Button>
                            )}
                            {isDraft && canPublish && (
                                unreviewedAi || videosMissingFile.length > 0 ? (
                                    <Tooltip title={unreviewedAi ? 'Review every AI-drafted module first - nothing AI-generated is published without a human review.' : `Upload a video for ${videosMissingFile.length} video module(s) first - a course cannot be published with an empty video module.`}>
                                        <span>{publishButton}</span>
                                    </Tooltip>
                                ) : (
                                    <Popconfirm
                                        title={`Publish "${course.title}" v${course.version}?`}
                                        description="Once published this version is immutable and can be assigned to learners. To change it later you fork a new version."
                                        okText="Publish"
                                        onConfirm={publish}
                                    >
                                        {publishButton}
                                    </Popconfirm>
                                )
                            )}
                        </Space>
                    )}

                    {isDraft && canAuthor && !canPublish && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="Only the Lead Advisor can publish a course."
                        />
                    )}
                    {isDraft && unreviewedAi && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="AI-drafted modules are waiting for human review"
                            description="Open each module marked “AI drafted - review”, correct it if needed, and use “Review & approve”. Publishing unlocks once none remain."
                        />
                    )}
                    {pendingTranslations > 0 && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message={`${pendingTranslations} translation(s) pending review`}
                            description="Approving every module in a language makes that language selectable for learners. It stays selectable only while the source text is unchanged; editing a module resets its translations to pending review."
                        />
                    )}

                    {(course.languageCoverage || []).length > 0 && (
                        <div data-testid="language-coverage" style={{ margin: '8px 0 12px' }}>
                            {course.languageCoverage.map((c) => (
                                <Text key={c.language} type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                    {languageLabel(c.language)}: {c.selectable ? 'offered to learners' : `not offered - ${(c.missingModuleIds || []).length} module(s) lack an approved translation of the current text`}
                                    {c.selectable && englishOnlyNote(c.englishOnlyModuleTypes) ? ` (${englishOnlyNote(c.englishOnlyModuleTypes)})` : ''}
                                </Text>
                            ))}
                        </div>
                    )}
                    {!isDraft && canAuthor && (course.modules || []).some((m) => (m.translations || []).length > 0) && (
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{TRANSLATIONS_LOCKED_MESSAGE}</Text>
                    )}
                    <Title level={5}>Modules</Title>
                    {(course.modules || []).length === 0 ? (
                        <Empty
                            description={isDraft && canAuthor ? 'No modules yet. Add one, or let AI draft an outline for you to review.' : 'This course has no modules.'}
                        />
                    ) : (
                        course.modules.map((m) => (
                            <ModuleCard
                                key={m.id}
                                module={m}
                                projectId={projectId}
                                canEditContent={canAuthor && isDraft}
                                onEdit={(mod) => setModuleModal({ open: true, module: mod })}
                                onChanged={refresh}
                            />
                        ))
                    )}
                </div>
            )}

            {canAuthor && (
                <>
                    <ModuleFormModal
                        open={moduleModal.open}
                        projectId={projectId}
                        courseId={courseId}
                        module={moduleModal.module}
                        onClose={() => setModuleModal({ open: false, module: null })}
                        onSaved={() => {
                            setModuleModal({ open: false, module: null });
                            refresh();
                        }}
                    />
                    <TranslateModal
                        open={translateOpen}
                        projectId={projectId}
                        courseId={courseId}
                        onClose={() => setTranslateOpen(false)}
                        onPartial={refresh}
                        onDone={() => {
                            setTranslateOpen(false);
                            refresh();
                        }}
                    />
                    <CourseFormModal
                        open={editOpen}
                        projectId={projectId}
                        course={course}
                        onClose={() => setEditOpen(false)}
                        onSaved={() => {
                            setEditOpen(false);
                            refresh();
                        }}
                    />
                    <EnrollModal
                        open={enrollOpen}
                        projectId={projectId}
                        course={course}
                        members={members}
                        onClose={() => setEnrollOpen(false)}
                        onDone={() => {
                            setEnrollOpen(false);
                            onChanged();
                        }}
                    />
                </>
            )}
        </Drawer>
    );
};

export default CourseDrawer;
